import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProjectService } from './project.service';
import { EntitlementsService } from './entitlements.service';
import { TokenEncryptionService } from './token-encryption.service';
import { MessagingServiceService } from './messaging-service.service';
import { planCodeDisplayName } from '../utils/billing-display.util';
import {
  getTargetPlanProfile,
  requiresTransitionValidation,
  TargetPlanProfile,
} from '../entitlements/plan-entitlements-catalog.util';
import {
  collectAdminControlDetails,
  collectEmployeeSettingViolations,
  collectGlobalSettingViolations,
  isAdminControlledEmployee,
  isHiddenModeEmployee,
  normalizeGlobalSettings,
  resolveGlobalSettingsForModeDetection,
  settingViolationLabels,
  isTruthySetting,
} from '../entitlements/plan-transition-settings.util';
import {
  enrichSettingDetail,
  resolveViolationFromLabel,
  splitSettingViolations,
} from '../entitlements/plan-transition-violation-catalog.util';
import { 
  getEmployeeTrackingMode, 
  getEmployeeSettingsRecord,
  collectHiddenModeDetails
} from '../entitlements/employee-tracking-mode.util';
import { getPlanRetentionLimits } from '../entitlements/plan-retention.util';
import {
  PlanTransitionAffectedEmployee,
  PlanTransitionIssue,
  PlanTransitionRetentionInfo,
  PlanTransitionValidationResponse,
} from '../user-plans/plan-transition.types';
import {
  DowngradeValidationDialogData,
  DowngradeValidationIssue,
} from '../components/downgrade-validation-dialog/downgrade-validation-dialog.component';

const ISSUE_ICONS: Record<string, string> = {
  hidden_mode: 'visibility_off',
  admin_control: 'admin_panel_settings',
  teams: 'groups',
  shifts: 'schedule',
  manager_roles: 'supervised_user_circle',
  employee_settings: 'person',
};

@Injectable({ providedIn: 'root' })
export class PlanTransitionValidationService {
  constructor(
    private projectService: ProjectService,
    private entitlementsService: EntitlementsService,
    private tokenEncryptionService: TokenEncryptionService,
    private messagingService: MessagingServiceService
  ) { }

  shouldValidate(currentPlanCode: string, targetPlanCode: string): boolean {
    return requiresTransitionValidation(currentPlanCode, targetPlanCode);
  }

  validateTransition(
    targetPlanCode: string,
    currentPlanCode: string,
    options?: { forceRefresh?: boolean }
  ): Observable<PlanTransitionValidationResponse> {
    const normalizedTarget = String(targetPlanCode || '').toLowerCase();
    const normalizedCurrent = String(currentPlanCode || '').toLowerCase();
    const forceRefresh = !!options?.forceRefresh;

    return this.projectService.getPlanTransitionValidation(normalizedTarget).pipe(
      map((raw) => raw as PlanTransitionValidationResponse),
      switchMap((apiReport) => {
        return this.buildClientSideValidation(
          normalizedTarget,
          normalizedCurrent,
          forceRefresh
        ).pipe(
          map((clientReport) => this.supplementRemediationIssues(apiReport, clientReport))
        );
      }),
      catchError(() =>
        this.buildClientSideValidation(normalizedTarget, normalizedCurrent, forceRefresh)
      )
    );
  }

  toDialogData(
    report: PlanTransitionValidationResponse
  ): DowngradeValidationDialogData {
    const informationalDetails =
      report.informational_setting_details?.length
        ? report.informational_setting_details
        : (report.informational_warnings || []).map((label) =>
          resolveViolationFromLabel(label)
        );

    return {
      targetPlanName:
        report.target_plan_name ||
        planCodeDisplayName(report.target_plan_code) ||
        report.target_plan_code,
      targetPlanCode: report.target_plan_code,
      currentPlanName:
        report.current_plan_name ||
        planCodeDisplayName(report.current_plan_code) ||
        report.current_plan_code,
      isValid: !!report.valid,
      issues: (report.issues || []).map((issue) => this.mapIssue(issue)),
      globalSettings: report.global_settings || [],
      globalSettingDetails:
        report.global_setting_details?.length
          ? report.global_setting_details
          : (report.global_settings || []).map((label) => resolveViolationFromLabel(label)),
      informationalSettingDetails: informationalDetails,
      warningMessages:
        report.warning_messages?.length
          ? report.warning_messages
          : this.defaultWarningMessages(report),
      retention: report.retention,
    };
  }

  private mapIssue(issue: PlanTransitionIssue): DowngradeValidationIssue {
    return {
      code: issue.code,
      icon: issue.icon || ISSUE_ICONS[issue.code] || 'warning',
      title: issue.title,
      description: issue.description,
      affectedEmployees: issue.affected_employees?.map((employee) => ({
        id: employee.id,
        name: employee.name,
        email: employee.email,
        details: employee.details || [],
        ssPersonalizeSettings: employee.ssPersonalizeSettings,
      })),
      affectedCount: issue.affected_count,
      isGlobalAdminControl: issue.is_global_admin_control,
    };
  }

  private buildClientSideValidation(
    targetPlanCode: string,
    currentPlanCode: string,
    forceRefresh = false
  ): Observable<PlanTransitionValidationResponse> {
    this.entitlementsService.syncFromSession();
    const targetProfile = getTargetPlanProfile(targetPlanCode);
    const companyId =
      this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id;

    return forkJoin({
      teams: this.projectService.getTeamList().pipe(catchError(() => of([]))),
      shifts: companyId
        ? this.projectService.getShifts(companyId).pipe(catchError(() => of({ shifts: [] })))
        : of({ shifts: [] }),
      employees: this.projectService
        .getUserList(0, 0, forceRefresh)
        .pipe(catchError(() => of([]))),
      managers: this.projectService.getFilteredTeam('manager').pipe(catchError(() => of([]))),
      leads: this.projectService.getFilteredTeam('lead').pipe(catchError(() => of([]))),
      globalSettings: this.projectService.getGlobalSettings().pipe(catchError(() => of({}))),
      firebaseUsers: this.getFirebaseUsersSnapshot(),
    }).pipe(
      map(({ teams, shifts, employees, managers, leads, globalSettings, firebaseUsers }) => {
        const modeGlobalSettings = resolveGlobalSettingsForModeDetection(
          globalSettings,
          this.tokenEncryptionService.getEncryptedItem('globalSettings')
        );

        return this.composeClientReport({
          targetPlanCode,
          currentPlanCode,
          targetProfile,
          teams: Array.isArray(teams) ? teams : [],
          shifts: Array.isArray(shifts?.shifts) ? shifts.shifts : [],
          employees: this.enrichEmployeesWithFirebase(
            this.filterEmployeeRoleUsers(this.normalizeUsers(employees)),
            firebaseUsers
          ),
          managers: this.normalizeUsers(managers),
          leads: this.normalizeUsers(leads),
          globalSettings: modeGlobalSettings,
        });
      })
    );
  }

  private composeClientReport(input: {
    targetPlanCode: string;
    currentPlanCode: string;
    targetProfile: TargetPlanProfile;
    teams: any[];
    shifts: any[];
    employees: any[];
    managers: any[];
    leads: any[];
    globalSettings: Record<string, unknown>;
  }): PlanTransitionValidationResponse {
    const issues: PlanTransitionIssue[] = [];
    const targetPlanName = planCodeDisplayName(input.targetPlanCode);
    const globalViolationDetails = collectGlobalSettingViolations(
      input.globalSettings,
      input.targetProfile,
      this.entitlementsService,
      input.employees
    );
    const { blocking: blockingGlobalDetails, informational: informationalGlobalDetails } =
      splitSettingViolations(globalViolationDetails);

    const hiddenEmployees = this.collectHiddenModeEmployees(
      input.employees,
      input.globalSettings,
      input.targetProfile
    );
    if (hiddenEmployees.length) {
      issues.push({
        code: 'hidden_mode',
        icon: ISSUE_ICONS.hidden_mode,
        title: 'Hidden Mode Switching',
        description: `Hidden Mode isn't available on the ${targetPlanName} plan. Select the employee below to switch them to Employee Controlled.`,
        affected_employees: hiddenEmployees,
        affected_count: hiddenEmployees.length,
      });
    }

    const adminControlEmployees = this.collectAdminControlEmployees(
      input.employees,
      input.globalSettings,
      input.targetProfile
    );
    if (adminControlEmployees.length) {
      const isGlobalAutoTracking = String(input.globalSettings?.ss_automatic_tracking ?? '').trim().toLowerCase() === 'true';

      const personalizedAdminControlEmployees = adminControlEmployees.filter(
        (emp) => !emp.details?.includes('Inheriting workspace Auto Tracking (Restricted Mode)')
      );

      let description = '';
      if (isGlobalAutoTracking) {
        if (personalizedAdminControlEmployees.length > 0) {
          description = `Admin Controlled tracking isn't available on the ${targetPlanName} plan. Switch your workspace to Employee Controlled and update ${personalizedAdminControlEmployees.length} employee${personalizedAdminControlEmployees.length === 1 ? "'s" : "s'"} personalized settings.`;
        } else {
          description = `Admin Controlled tracking isn't available on the ${targetPlanName} plan. Switch your workspace to Employee Controlled.`;
        }
      } else {
        description = `Admin Controlled tracking isn't available on the ${targetPlanName} plan. Update ${adminControlEmployees.length} employee${adminControlEmployees.length === 1 ? "'s" : "s'"} personalized settings.`;
      }

      issues.push({
        code: 'admin_control',
        icon: ISSUE_ICONS.admin_control,
        title: 'Admin Control Mode Switching',
        description,
        affected_employees: isGlobalAutoTracking ? personalizedAdminControlEmployees : adminControlEmployees,
        affected_count: adminControlEmployees.length,
        is_global_admin_control: isGlobalAutoTracking,
      });
    }

    const teamsUsed = input.teams.length;
    if (
      input.targetProfile.teamsMax !== null &&
      teamsUsed > input.targetProfile.teamsMax
    ) {
      issues.push({
        code: 'teams',
        icon: ISSUE_ICONS.teams,
        title: 'Team Management',
        description: `Your workspace currently contains ${teamsUsed} teams. The selected plan supports a maximum of ${input.targetProfile.teamsMax} teams.`,
        affected_count: teamsUsed,
      });
    }

    const activeShifts = input.shifts.length;
    if (
      input.targetProfile.shiftsMax !== null &&
      activeShifts > input.targetProfile.shiftsMax
    ) {
      issues.push({
        code: 'shifts',
        icon: ISSUE_ICONS.shifts,
        title: 'Shift Configuration',
        description: `Your workspace currently uses ${activeShifts} active shifts. The selected plan supports only ${input.targetProfile.shiftsMax} shift${input.targetProfile.shiftsMax === 1 ? '' : 's'}.`,
        affected_count: activeShifts,
      });
    }

    const managerCount = input.managers.length;
    const leadCount = input.leads.length;
    if (
      managerCount + leadCount > 0 &&
      !input.targetProfile.companyEntitlements.has('manager_roles')
    ) {
      const managerRoleEmployees = this.collectManagerRoleEmployees(
        input.managers,
        input.leads
      );
      issues.push({
        code: 'manager_roles',
        icon: ISSUE_ICONS.manager_roles,
        title: 'Manager / Team Lead Roles',
        description: `Manager and Team Lead roles aren't available on the Basic plan. Click the affected employees below to update their roles to Employee.`,
        affected_employees: managerRoleEmployees,
        affected_count: managerRoleEmployees.length,
      });
    }

    const employeeViolations = this.collectPersonalizedEmployeeViolations(
      input.employees,
      input.globalSettings,
      input.targetProfile
    );
    if (employeeViolations.length) {
      issues.push({
        code: 'employee_settings',
        icon: ISSUE_ICONS.employee_settings,
        title: 'Personalized Employee Settings',
        description: `${employeeViolations.length} employee${employeeViolations.length === 1 ? '' : 's'} have personalized monitoring overrides that exceed ${targetPlanName} plan limits. Update each employee's settings individually.`,
        affected_employees: employeeViolations,
        affected_count: employeeViolations.length,
      });
    }

    const valid = issues.length === 0 && blockingGlobalDetails.length === 0;
    const retention = this.buildRetentionInfo(
      input.targetPlanCode,
      input.currentPlanCode
    );

    return {
      valid,
      current_plan_code: input.currentPlanCode,
      target_plan_code: input.targetPlanCode,
      current_plan_name: planCodeDisplayName(input.currentPlanCode),
      target_plan_name: targetPlanName,
      issues,
      global_settings: settingViolationLabels(blockingGlobalDetails),
      global_setting_details: blockingGlobalDetails.map(({ route, ...detail }) => detail),
      informational_warnings: settingViolationLabels(informationalGlobalDetails),
      informational_setting_details: informationalGlobalDetails.map(
        ({ route, ...detail }) => detail
      ),
      warning_messages: this.buildWarningMessages(retention, valid),
      retention,
    };
  }

  private collectAdminControlEmployees(
    employees: any[],
    globalSettings: Record<string, unknown>,
    targetProfile: TargetPlanProfile
  ): PlanTransitionAffectedEmployee[] {
    if (targetProfile.companyEntitlements.has('advanced_tracking_controls')) {
      return [];
    }

    return employees
      .filter((employee) => isAdminControlledEmployee(employee, globalSettings))
      .map((employee) => ({
        id: employee.id,
        name: employee.name || employee.slug || `Employee #${employee.id}`,
        email: employee.email || employee.username || '',
        details: collectAdminControlDetails(employee, globalSettings),
      }));
  }

  private collectManagerRoleEmployees(
    managers: any[],
    leads: any[]
  ): PlanTransitionAffectedEmployee[] {
    const affected: PlanTransitionAffectedEmployee[] = [];

    managers.forEach((user) => {
      affected.push({
        id: user.id,
        name: user.name || user.slug || `User #${user.id}`,
        email: user.email || user.username || '',
        details: ['Manager role'],
      });
    });

    leads.forEach((user) => {
      affected.push({
        id: user.id,
        name: user.name || user.slug || `User #${user.id}`,
        email: user.email || user.username || '',
        details: ['Team lead role'],
      });
    });

    return affected.sort((a, b) => a.name.localeCompare(b.name));
  }

  private formatRoleAssignmentLabel(managerCount: number, leadCount: number): string {
    const parts: string[] = [];
    if (managerCount) {
      parts.push(`${managerCount} manager${managerCount === 1 ? '' : 's'}`);
    }
    if (leadCount) {
      parts.push(`${leadCount} team lead${leadCount === 1 ? '' : 's'}`);
    }
    const verb = managerCount + leadCount === 1 ? 'is' : 'are';
    return `${parts.join(' and ')} ${verb} assigned`;
  }

  private collectHiddenModeEmployees(
    employees: any[],
    globalSettings: Record<string, unknown>,
    targetProfile: TargetPlanProfile
  ): PlanTransitionAffectedEmployee[] {
    if (targetProfile.companyEntitlements.has('hidden_mode')) {
      return [];
    }

    return employees
      .filter((employee) => isHiddenModeEmployee(employee, globalSettings))
      .map((employee) => ({
        id: employee.id,
        name: employee.name || employee.slug || `Employee #${employee.id}`,
        email: employee.email || employee.username || '',
        details: collectHiddenModeDetails(employee, globalSettings),
      }));
  }

  private collectPersonalizedEmployeeViolations(
    employees: any[],
    globalSettings: Record<string, unknown>,
    targetProfile: TargetPlanProfile
  ): PlanTransitionAffectedEmployee[] {
    const affected: PlanTransitionAffectedEmployee[] = [];

    employees.forEach((employee) => {
      const details = collectEmployeeSettingViolations(
        employee,
        globalSettings,
        targetProfile,
        this.entitlementsService
      ).filter((detail) => detail !== 'Hidden Mode enabled');

      if (!details.length) {
        return;
      }

      affected.push({
        id: employee.id,
        name: employee.name || employee.slug || `Employee #${employee.id}`,
        details,
        ssPersonalizeSettings: isTruthySetting(getEmployeeSettingsRecord(employee).ss_personalizeSettings)
      });
    });

    return affected;
  }

  private buildRetentionInfo(targetPlanCode: string, currentPlanCode?: string) {
    const extended = this.entitlementsService.hasCompany('extended_retention');
    const targetLimits = getPlanRetentionLimits(targetPlanCode, true, false);
    const currentLimits = getPlanRetentionLimits(
      currentPlanCode || targetPlanCode,
      true,
      extended
    );
    const gracePeriodDays = 7;
    const cleanupDate = new Date();
    cleanupDate.setDate(cleanupDate.getDate() + gracePeriodDays);

    const affectedTo = new Date();
    affectedTo.setDate(
      affectedTo.getDate() - targetLimits.screenshotsRetentionDays
    );

    return {
      grace_period_days: gracePeriodDays,
      cleanup_scheduled_at: cleanupDate.toISOString(),
      affected_date_range: {
        from: 'Beginning of retention window',
        to: formatDisplayDate(affectedTo),
      },
      target_screenshots_retention_days: targetLimits.screenshotsRetentionDays,
      current_screenshots_retention_days: currentLimits.screenshotsRetentionDays,
    };
  }

  private buildWarningMessages(
    retention: PlanTransitionRetentionInfo & {
      target_screenshots_retention_days: number;
    },
    valid: boolean
  ): string[] {
    const messages = [];

    if (retention.cleanup_scheduled_at) {
      const cleanupLabel =
        formatDisplayDate(new Date(retention.cleanup_scheduled_at)) ||
        retention.cleanup_scheduled_at;
      const currentDays = retention.current_screenshots_retention_days;
      const targetDays = retention.target_screenshots_retention_days;
      const retentionDetail =
        currentDays && targetDays && currentDays > targetDays
          ? `Screenshot retention drops from <b>${currentDays}</b> to <b>${targetDays}</b> days. `
          : '';
      messages.push(
        `${retentionDetail}Data outside the <b>${targetDays}-day</b> retention window will remain accessible during a <b>${retention.grace_period_days}-day</b> grace period (until <b>${cleanupLabel}</b>). You can export, upgrade, or fix settings before cleanup runs.`
      );
    }

    if (valid) {
      messages.push(
        'After the grace period, retained screenshots and logs outside the new plan window are deleted consistently and cannot be restored by a later upgrade.'
      );
    } else {
      messages.push(
        'Resolve the incompatibilities below before downgrading. SuperSee will not delete teams, shifts, or roles automatically.'
      );
    }

    return messages;
  }

  private defaultWarningMessages(
    report: PlanTransitionValidationResponse
  ): string[] {
    if (report.warning_messages?.length) {
      return report.warning_messages;
    }
    const retention = report.retention
      ? {
        ...this.buildRetentionInfo(
          report.target_plan_code,
          report.current_plan_code
        ),
        ...report.retention,
        target_screenshots_retention_days:
          report.retention.target_screenshots_retention_days ??
          this.buildRetentionInfo(
            report.target_plan_code,
            report.current_plan_code
          ).target_screenshots_retention_days,
      }
      : this.buildRetentionInfo(
        report.target_plan_code,
        report.current_plan_code
      );
    return this.buildWarningMessages(retention, report.valid);
  }

  private normalizeUsers(raw: unknown): any[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw && typeof raw === 'object' && Array.isArray((raw as any).data)) {
      return (raw as any).data;
    }
    return [];
  }

  private filterEmployeeRoleUsers(users: any[]): any[] {
    return users.filter((user) => {
      const roles = Array.isArray(user?.role) ? user.role : [];
      return roles.some((role) => String(role).toLowerCase() === 'employee');
    });
  }

  private getFirebaseUsersSnapshot(): Observable<any[]> {
    const cached = this.messagingService.getLatestFirebaseUsersSnapshot();
    return of(this.normalizeFirebaseUsers(cached));
  }

  private normalizeFirebaseUsers(raw: unknown): any[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw && typeof raw === 'object') {
      return Object.entries(raw as Record<string, unknown>)
        .filter(([, value]) => value && typeof value === 'object')
        .map(([key, value]) => ({
          ...(value as Record<string, unknown>),
          id: (value as any)?.id ?? key,
        }));
    }
    return [];
  }

  private enrichEmployeesWithFirebase(employees: any[], firebaseUsers: any[]): any[] {
    if (!firebaseUsers.length) {
      return employees;
    }

    const firebaseById = new Map<string, any>();
    firebaseUsers.forEach((user) => {
      if (user?.id != null) {
        firebaseById.set(String(user.id), user);
      }
    });

    return employees.map((employee) => {
      const firebaseUser = firebaseById.get(String(employee?.id));
      if (!firebaseUser?.appInfo?.version && !firebaseUser?.appInfo?.applicationType) {
        return employee;
      }

      const rawAppType = firebaseUser?.appInfo?.applicationType;
      const appType = rawAppType ? String(rawAppType) : 'General';
      const label = firebaseUser?.appInfo?.label ? String(firebaseUser.appInfo.label).toUpperCase() : appType.charAt(0).toUpperCase();

      return {
        ...employee,
        appInfo: {
          ...firebaseUser.appInfo,
          label,
          applicationType: appType.charAt(0).toUpperCase() + appType.slice(1),
        },
      };
    });
  }

  private supplementRemediationIssues(
    report: PlanTransitionValidationResponse,
    clientReport: PlanTransitionValidationResponse
  ): PlanTransitionValidationResponse {
    let issues = [...(report.issues || [])];

    for (const code of ['hidden_mode', 'admin_control', 'manager_roles', 'teams', 'shifts', 'employee_settings'] as const) {
      const clientIssue = clientReport.issues?.find((issue) => issue.code === code);
      if (!clientIssue) {
        continue;
      }

      const existingIndex = issues.findIndex((issue) => issue.code === code);
      const clientCount = clientIssue.affected_count ?? clientIssue.affected_employees?.length ?? 0;
      const existingCount =
        existingIndex >= 0 ? (issues[existingIndex].affected_count ?? issues[existingIndex].affected_employees?.length ?? 0) : 0;

      if (existingIndex >= 0 && clientCount <= existingCount && !clientIssue.is_global_admin_control) {
        continue;
      }

      const nextIssue = {
        ...clientIssue,
        icon: clientIssue.icon || ISSUE_ICONS[code],
      };

      if (existingIndex >= 0) {
        issues[existingIndex] = nextIssue;
        continue;
      }

      const hiddenIndex = issues.findIndex((issue) => issue.code === 'hidden_mode');
      const insertAt =
        code === 'admin_control' && hiddenIndex >= 0 ? hiddenIndex + 1 : issues.length;
      issues.splice(insertAt, 0, nextIssue);
    }

    const globalSettings = this.mergeGlobalSettingViolations(
      report.global_settings || [],
      clientReport.global_settings || [],
      report.global_setting_details || [],
      clientReport.global_setting_details || [],
      report.informational_setting_details || [],
      clientReport.informational_setting_details || [],
      report.informational_warnings || [],
      clientReport.informational_warnings || []
    );

    return this.finalizeReport({
      ...report,
      target_plan_code: report.target_plan_code || clientReport.target_plan_code,
      current_plan_code: report.current_plan_code || clientReport.current_plan_code,
      issues,
      global_settings: globalSettings.allLabels,
      global_setting_details: globalSettings.allDetails,
      warning_messages:
        report.warning_messages?.length
          ? report.warning_messages
          : clientReport.warning_messages,
      retention: report.retention ?? clientReport.retention,
    });
  }

  private finalizeReport(
    report: PlanTransitionValidationResponse
  ): PlanTransitionValidationResponse {
    const allDetails = (report.global_setting_details?.length
      ? report.global_setting_details
      : (report.global_settings || []).map((label) => resolveViolationFromLabel(label))
    ).map((detail) => enrichSettingDetail(detail as Parameters<typeof enrichSettingDetail>[0]));

    const existingInformational = (report.informational_setting_details || []).map((detail) =>
      enrichSettingDetail(detail as Parameters<typeof enrichSettingDetail>[0])
    );
    for (const label of report.informational_warnings || []) {
      const resolved = resolveViolationFromLabel(label);
      if (!existingInformational.some((item) => item.label === resolved.label)) {
        existingInformational.push(resolved);
      }
    }

    const combined = [...allDetails, ...existingInformational];
    const { blocking, informational } = splitSettingViolations(combined);

    return {
      ...report,
      issues: report.issues || [],
      global_settings: settingViolationLabels(blocking),
      global_setting_details: blocking.map(({ route, ...detail }) => detail),
      informational_warnings: settingViolationLabels(informational),
      informational_setting_details: informational.map(({ route, ...detail }) => detail),
      valid: (report.issues || []).length === 0 && blocking.length === 0,
    };
  }

  private mergeGlobalSettingViolations(
    apiList: string[],
    clientList: string[],
    apiDetails: Array<{ code: string; label: string; fixHint: string; where: string }>,
    clientDetails: Array<{ code: string; label: string; fixHint: string; where: string }>,
    apiInformationalDetails: Array<{ code: string; label: string; fixHint: string; where: string }> = [],
    clientInformationalDetails: Array<{ code: string; label: string; fixHint: string; where: string }> = [],
    apiInformationalLabels: string[] = [],
    clientInformationalLabels: string[] = []
  ): {
    allLabels: string[];
    allDetails: Array<{ code: string; label: string; fixHint: string; where: string }>;
  } {
    const details = [...(apiDetails || []), ...(apiInformationalDetails || [])];
    const labels = new Set(details.map((item) => item.label));

    for (const detail of clientDetails || []) {
      if (labels.has(detail.label)) {
        continue;
      }
      const isIntervalRow = /screenshot interval|periodic screenshot/i.test(detail.label);
      const apiHasInterval = details.some((item) =>
        /screenshot interval|periodic screenshot/i.test(item.label)
      );
      if (isIntervalRow && apiHasInterval) {
        continue;
      }
      details.push(detail);
      labels.add(detail.label);
    }

    for (const detail of clientInformationalDetails || []) {
      if (labels.has(detail.label)) {
        continue;
      }
      details.push(detail);
      labels.add(detail.label);
    }

    for (const label of [...(clientList || []), ...(clientInformationalLabels || [])]) {
      if (labels.has(label)) {
        continue;
      }
      details.push(resolveViolationFromLabel(label));
      labels.add(label);
    }

    for (const label of [...(apiList || []), ...(apiInformationalLabels || [])]) {
      if (!labels.has(label)) {
        details.push(resolveViolationFromLabel(label));
        labels.add(label);
      }
    }

    return {
      allLabels: details.map((item) => item.label),
      allDetails: details,
    };
  }
}

function formatDisplayDate(value: Date): string {
  return value.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
